import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { formatTextWithVariables } from '../../../components/common/FormatTextWithVariables';
import axios from '../../../libs/axios';
import { PublicRateLimitState } from '../../../components/common/PublicRateLimitState';
import DefaultLayout from '../../../layouts/default';
import BaseSection from '../../../components/base/Section';
import HeaderBanner from '../../../components/sections/HeaderBanner';
import { TemplateValuesModal } from '../../../components/PromptLibrary/modals/TemplateValuesModal';

type SharedPrompt = {
  id: string;
  title: string;
  prompt: string;
  section: string;
  labels: string[];
  tool: {
    id: string;
    name: string;
    icon: string;
  } | null;
  type: string;
  createdBy: string;
  createdAt: string;
};

const PublicPromptPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  
  const [prompt, setPrompt] = useState<SharedPrompt | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [showContent, setShowContent] = useState(true);
  const [isTemplateModalOpen, setIsTemplateModalOpen] = useState(false);
  const [currentTemplateText, setCurrentTemplateText] = useState('');
  
  useEffect(() => {
    if (!id) return;
    
    const fetchPrompt = async () => {
      try {
        setLoading(true);
        
        // Use axios instead of fetch for consistent handling
        const response = await axios.get(`/public/prompt-library/prompt/${id}`);
        
        // Axios automatically throws for non-2xx responses, so we don't need to check response.ok
        const data = response.data;
        setPrompt(data);
      } catch (err: any) { // Type assertion to handle TypeScript errors
        // Handle different error types
        if (err.response) {
          // The request was made and the server responded with a status code
          // that falls out of the range of 2xx
          setError(err.response.data.message || 'Failed to load prompt');
        } else if (err.request) {
          // The request was made but no response was received
          setError('No response from server. Please try again later.');
        } else {
          // Something happened in setting up the request that triggered an Error
          setError('An unexpected error occurred');
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchPrompt();
  }, [id]);
  
  // Check if text contains template placeholders
  const containsTemplatePlaceholders = (text: string): boolean => {
    const placeholderRegex = /\{\{([^{}]+)\}\}/g;
    return placeholderRegex.test(text);
  };

  // Add XML formatting to the prompt
  const addPromptXmlFormat = (promptText: string, section: string): string => {
    return `<${section}>${promptText}</${section}>`;
  };

  const handleCopyClick = () => {
    if (!prompt) return;
    
    // Format the prompt with XML tags
    const formattedPrompt = addPromptXmlFormat(prompt.prompt, prompt.section);
    
    // Check if the prompt contains template placeholders
    if (containsTemplatePlaceholders(formattedPrompt)) {
      // Open the template values modal
      setCurrentTemplateText(formattedPrompt);
      setIsTemplateModalOpen(true);
    } else {
      // No templates, copy directly
      navigator.clipboard.writeText(formattedPrompt)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(() => {});
    }
  };
  
  // Handle the submission of the template values modal
  const handleTemplateSubmit = (filledTemplate: string) => {
    navigator.clipboard.writeText(filledTemplate);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  
  // Set document title when prompt changes
  useEffect(() => {
    document.title = prompt ? `${prompt.title} | Shared Prompt` : 'Shared Prompt';
  }, [prompt]);
  
  return (
    <DefaultLayout>
      {/* Hero Section with Gradient Background */}
      <section className="w-full lg:pb-0 dark:bg-slate-900">
        <HeaderBanner title="Framework"
          buttons={[
            {title: "Go to library", url: "/dashboard/prompt-library"},
            {title: "Learn Prompt Engineering", url: "/documentation/large-language-models#llms"},
            {title: "Find tools", url: "/tools"},
          ]}
        />
      </section>

      {/* Content Section */}
      <section className="w-full py-12 dark:bg-gray-900">
        <BaseSection>
          <div className="mb-16 col-span-12 px-4 sm:px-6">
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
              </div>
            ) : error ? (
              <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl p-8 text-center max-w-2xl mx-auto">
                {(error.includes('Rate limit') || error.includes('Too Many Attempts')) ? (
                  <PublicRateLimitState />
                ) : (
                  <>
                    <div className="text-red-500 dark:text-red-400 mb-6">
                      <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{error}</h2>
                    <p className="text-gray-600 dark:text-gray-300 mb-6 text-lg">
                      Please check the URL or try again later.
                    </p>
                    <Link to="/dashboard/prompt-library" className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-gradient-to-r from-[#468ef9] to-[#0c66ee] hover:from-[#3a7ad9] hover:to-[#0a56c7] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                      Go to Prompt Library
                    </Link>
                  </>
                )}
              </div>
            ) : prompt ? (
              <div className="bg-white dark:bg-gray-800 shadow-xl rounded-xl overflow-hidden transform transition-all duration-300 hover:shadow-2xl">
                {/* Prompt header with gradient background */}
                <div className="px-8 py-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-gray-800 dark:to-gray-900">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <h2 className="text-2xl mb-0 font-bold text-gray-900 dark:text-white">{prompt.title}</h2>
                    <div className="flex flex-wrap gap-2">
                      {/* Section badge with gradient */}
                      <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white
                        ${prompt.section === 'Context' ? 'bg-gradient-to-r from-red-500 to-red-600' : ''}
                        ${prompt.section === 'Register' ? 'bg-gradient-to-r from-purple-500 to-purple-600' : ''}
                        ${prompt.section === 'Acting Role' ? 'bg-gradient-to-r from-green-500 to-green-600' : ''}
                        ${prompt.section === 'Format' ? 'bg-gradient-to-r from-yellow-500 to-yellow-600' : ''}
                        ${prompt.section === 'Task' ? 'bg-gradient-to-r from-blue-500 to-blue-600' : ''}
                        ${!['Context', 'Register', 'Acting Role', 'Format', 'Task'].includes(prompt.section) ? 'bg-gradient-to-r from-gray-500 to-gray-600' : ''}
                        shadow-sm transform transition-transform duration-200 hover:scale-105
                      `}>
                        {prompt.section}
                      </span>
                      
                      {/* Type badge */}
                      {prompt.type && (
                        <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium text-white
                          ${prompt.type === 'example' ? 'bg-gradient-to-r from-green-500 to-green-600' : ''}
                          ${prompt.type === 'template' ? 'bg-gradient-to-r from-cyan-500 to-cyan-600' : ''}
                          ${!['example', 'template'].includes(prompt.type) ? 'bg-gradient-to-r from-blue-500 to-blue-600' : ''}
                          shadow-sm transform transition-transform duration-200 hover:scale-105
                        `}>
                          {prompt.type.charAt(0).toUpperCase() + prompt.type.slice(1)}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  {/* Tool and creator info */}
                  <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-gray-600 dark:text-gray-300">
                    {prompt.tool && (
                      <div className="flex items-center">
                        <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                        <span className="font-medium">{prompt.tool.name}</span>
                      </div>
                    )}
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                      <span className="font-medium">{prompt.createdBy}</span>
                    </div>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                      <span>{prompt.createdAt}</span>
                    </div>
                  </div>
                </div>
                
                {/* Prompt content with improved styling */}
                <div className="px-8 py-6">
                  <button
                    onClick={() => setShowContent(prev => !prev)}
                    className="w-full flex items-center justify-between text-xl font-semibold text-gray-900 dark:text-white mb-4 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                  >
                    <span className="flex items-center">
                      <svg className="w-6 h-6 mr-2 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="text-sm">Prompt Content</span>
                    </span>
                    <svg 
                      className={`w-5 h-5 transition-transform duration-200 ${showContent ? 'transform rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  <div className={`
                    grid transition-all duration-300 ease-in-out
                    ${showContent ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'}`}
                  >
                    <div className="overflow-hidden">
                      <div className="prose prose-lg max-w-none dark:prose-invert">
                        <div className="relative">
                          <pre className="whitespace-pre-wrap text-gray-800 dark:text-gray-200 bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-700 overflow-auto shadow-inner">
                            {formatTextWithVariables(prompt.prompt || '')}
                          </pre>
                          {/* Decorative element */}
                          <div className="absolute -top-4 -right-4 w-20 h-20 bg-gradient-to-br from-blue-400/10 to-purple-500/10 rounded-full blur-xl"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Labels section if present */}
                {prompt.labels && prompt.labels.length > 0 && (
                  <div className="px-8 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                    <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Labels</h3>
                    <div className="flex flex-wrap gap-2">
                      {prompt.labels.map((label, index) => (
                        <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Action buttons with gradient */}
                <div className="px-8 py-6 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 border-t border-gray-200 dark:border-gray-700">
                  <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                    <Link to="/dashboard/prompt-library" className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200">
                      <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                      </svg>
                      Back to Library
                    </Link>
                    <button
                      onClick={handleCopyClick}
                      className="inline-flex items-center px-6 py-1 border border-transparent text-base font-medium rounded-md shadow-md text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                    >
                      <svg className="mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" />
                      </svg>
                      {copied ? 'Copied to Clipboard!' : 'Copy Prompt'}
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex justify-center items-center h-64">
                <div className="text-center">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">No prompt found</h2>
                  <p className="text-gray-600 dark:text-gray-300">
                    The prompt you're looking for doesn't exist or has been removed.
                  </p>
                </div>
              </div>
            )}
          </div>
        </BaseSection>
      </section>
      
      {/* Template Values Modal */}
      <TemplateValuesModal
        isOpen={isTemplateModalOpen}
        onClose={() => setIsTemplateModalOpen(false)}
        onSubmit={handleTemplateSubmit}
        templateText={currentTemplateText}
      />
    </DefaultLayout>
  );
};

export default PublicPromptPage;